'use client';

/**
 * HubSpot form embed. Supports BOTH:
 *
 * <ol>
 *   <li>Legacy v2 forms via {@code hbspt.forms.create({ portalId, formId, region })}</li>
 *   <li>Newer shareable forms via {@code shareUrl} (iframe)</li>
 * </ol>
 *
 * <p>Provide EITHER {@code portalId + formId} OR {@code shareUrl}.
 * {@code shareUrl} wins if both are set.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EmbedCard,
  LoadingState,
  ConfigurationError,
  FileTextIcon,
  useStableId,
  useExternalScript,
} from './_shared';

interface HubSpotFormBlockProps {
  title?: string;
  portalId?: string;
  formId?: string;
  /** Default 'na1'. */
  region?: string;
  /** Newer share URL ({@code https://share.hsforms.com/...}). Takes
   *  precedence over portalId/formId. */
  shareUrl?: string;
  iframeHeight?: number;
}

const HUBSPOT_PORTAL_RE = /^[0-9]{4,12}$/;
const HUBSPOT_FORM_RE = /^[A-Za-z0-9-]{6,64}$/;
const HUBSPOT_SHARE_RE = /^https:\/\/[a-z0-9-]*\.?hsforms\.com\/[A-Za-z0-9_/?=&%.-]+$/;

export function HubSpotFormBlock(props: HubSpotFormBlockProps) {
  const { title, shareUrl, iframeHeight = 600 } = props;
  const useShareUrl = !!(shareUrl && shareUrl.trim().length > 0);
  if (useShareUrl) {
    return <ShareUrlEmbed title={title} shareUrl={shareUrl} iframeHeight={iframeHeight} />;
  }
  return (
    <V2ApiEmbed
      title={title}
      portalId={props.portalId}
      formId={props.formId ?? ''}
      region={props.region ?? 'na1'}
    />
  );
}

function ShareUrlEmbed({
  title,
  shareUrl,
  iframeHeight,
}: {
  title?: string;
  shareUrl: string;
  iframeHeight: number;
}) {
  const { t } = useTranslation('site-builder-blocks');
  if (!HUBSPOT_SHARE_RE.test(shareUrl)) {
    return <ConfigurationError message={t('embeds.hubspotForm.invalidShareUrl')} />;
  }
  return (
    <EmbedCard title={title} accent={<FileTextIcon />}>
      <iframe
        src={shareUrl}
        title={title ?? t('embeds.hubspotForm.frameTitle')}
        loading="lazy"
        style={{
          width: '100%',
          minHeight: iframeHeight,
          border: 'none',
          background: 'transparent',
        }}
      />
    </EmbedCard>
  );
}

function V2ApiEmbed({
  title,
  portalId,
  formId,
  region,
}: {
  title?: string;
  portalId?: string;
  formId: string;
  region: string;
}) {
  const { t } = useTranslation('site-builder-blocks');
  const containerId = useStableId('hs-form');
  const validPortal = !!portalId && HUBSPOT_PORTAL_RE.test(portalId);
  const validForm = HUBSPOT_FORM_RE.test(formId);
  const scriptReady = useExternalScript(
    validPortal && validForm ? 'https://js.hsforms.net/forms/embed/v2.js' : null,
  );
  const [created, setCreated] = useState(false);

  useEffect(() => {
    if (!scriptReady || created || !validForm || !validPortal) return;
    const w = window as unknown as {
      hbspt?: { forms: { create: (cfg: Record<string, unknown>) => void } };
    };
    if (!w.hbspt) return;
    try {
      w.hbspt.forms.create({
        region,
        portalId,
        formId,
        target: `#${containerId}`,
      });
      setCreated(true);
    } catch {
      /* silent — HubSpot will log */
    }
  }, [scriptReady, created, region, portalId, formId, containerId, validForm, validPortal]);

  if (!portalId && !formId) {
    return <ConfigurationError message={t('embeds.hubspotForm.missingConfig')} />;
  }
  if (!validPortal) {
    return <ConfigurationError message={t('embeds.hubspotForm.invalidPortalId')} />;
  }
  if (!validForm) {
    return <ConfigurationError message={t('embeds.hubspotForm.invalidFormId')} />;
  }

  return (
    <EmbedCard title={title} accent={<FileTextIcon />}>
      <div id={containerId} />
      {!created && <LoadingState label={t('embeds.hubspotForm.loading')} />}
    </EmbedCard>
  );
}

export type { HubSpotFormBlockProps };
